import React, { useEffect, useState } from 'react';
import { Filter } from './GridConfigurator';

interface FilterConfiguratorProps {
  isAdmin: boolean;
  availableFilters: string[];
  filters: Filter[];
  onFilterChange: (newFilters: Filter[]) => void;
}

const FilterConfigurator: React.FC<FilterConfiguratorProps> = (
  {
    isAdmin,
    availableFilters,
    filters,
    onFilterChange
  }) => {
  const [editingFilters, setEditingFilters] = useState<Filter[]>([...filters]);

  useEffect(() => {
    onFilterChange(editingFilters);
  }, [editingFilters]);

  const toTitleCase = (input: string): string => {
    if (!input) return '';

    return input
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\./g, ' ')
      .toLowerCase()
      .replace(/^./, c => c.toUpperCase());
  };

  const handleAdd = () => setEditingFilters([...editingFilters, { fieldName: '', caption: '', editor: '', isVisible: true, isRequired: false }]);

  const handleRemove = (index: number) => {
    const newFilters = [...editingFilters];
    newFilters.splice(index, 1);
    setEditingFilters(newFilters);
  };

  const handleChange = (index: number, key: keyof Filter, value: string) => {
    const newFilters = [...editingFilters];
    if (key === 'fieldName' && value && !newFilters[index].isRequired) {
      newFilters[index].fieldName = value;
      newFilters[index].caption = toTitleCase(value);
    } else if (key === 'caption' && value) {
      newFilters[index].caption = value;
    } else if (key === 'editor' && value) {
      newFilters[index].editor = value;
    } else if (key === 'isVisible') {
      newFilters[index].isVisible = value === 'true';
    } else if (key === 'isRequired') {
      newFilters[index].isRequired = value === 'true';
      if (newFilters[index].isRequired) {
        newFilters[index].isVisible = true;
      }
    }
    setEditingFilters(newFilters);
  };

  const handleDragStart = (e: React.DragEvent<HTMLSpanElement>, index: number) => {
    if (e.target instanceof HTMLElement && e.target.classList.contains('drag-handle')) {
      e.dataTransfer.setData('text/plain', index.toString());
    } else {
      e.preventDefault();
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetIndex: number) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (sourceIndex === targetIndex) return;
    const newFilters = [...editingFilters];
    const [movedFilter] = newFilters.splice(sourceIndex, 1);
    newFilters.splice(targetIndex, 0, movedFilter);
    setEditingFilters(newFilters);
  };

  const getAvailableFieldsFor = (currentField: string, isRequired: boolean) =>
    isRequired
      ? [currentField] // Only allow the current field if required
      : availableFilters.filter(
        f => f === currentField || !editingFilters.some(c => c.fieldName === f)
      );

  return <>
    <table className="table table-bordered">
      <thead>
      <tr>
        <th></th>
        <th>{window.ifunds.strings.show}</th>
        <th>{window.ifunds.strings.fieldName}</th>
        {isAdmin ? <th>{window.ifunds.strings.caption}</th> : undefined}
        {isAdmin ? <th>Editor</th> : undefined}
        {isAdmin ? <th>{window.ifunds.strings.required}</th> : undefined}
        {isAdmin ? <th></th> : undefined}
      </tr>
      </thead>
      <tbody>
      {editingFilters.map((filter, index) => <tr
        key={index}
        onDragOver={handleDragOver}
        onDrop={e => handleDrop(e, index)}
      >
        <td className="align-middle w-0">
          <span
            className="drag-handle"
            style={{ cursor: 'grab' }}
            draggable
            onDragStart={e => handleDragStart(e, index)}
          >
            ☰
          </span>
        </td>
        <td className="align-middle text-center w-0">
          <input
            onChange={e => handleChange(index, 'isVisible', e.target.checked ? 'true' : 'false')}
            checked={filter.isVisible}
            className="form-check-input"
            type="checkbox"
            disabled={filter.isRequired}
          />
        </td>
        {isAdmin ?
          <td>
            <select
              className="form-select"
              value={filter.fieldName}
              onChange={e => handleChange(index, 'fieldName', e.target.value)}
              required
            >
              <option value="" disabled>
                Select Field
              </option>
              {getAvailableFieldsFor(filter.fieldName, filter.isRequired).map(field => <option key={field} value={field}>
                {field}
              </option>)}
            </select>
          </td>
          :
          <td>
            {filter.caption}
          </td>
        }
        {isAdmin ?
          <>
            <td>
              <input
                type="text"
                className="form-control"
                value={filter.caption}
                onChange={e => handleChange(index, 'caption', e.target.value)}
                placeholder="Caption"
                required
              />
            </td>
            <td>
              <select
                className="form-select"
                value={filter.editor}
                onChange={e => handleChange(index, 'editor', e.target.value)}
                required
              >
                <option value="" disabled>
                  Select Editor
                </option>
                <option key="Dropdown" value="Dropdown">Dropdown</option>
                <option key="Multiselect" value="Multiselect">Multiselect</option>
                <option key="TextBox" value="TextBox">TextBox</option>
              </select>
            </td>
            <td className="align-middle text-center">
              <input
                onChange={e => handleChange(index, 'isRequired', e.target.checked ? 'true' : 'false')}
                checked={filter.isRequired}
                className="form-check-input"
                type="checkbox"
              />
            </td>
            <td>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleRemove(index)}
              >
                <i className="fas fa-remove"></i>
              </button>
            </td>
          </>
          : undefined
        }
      </tr>)}
      </tbody>
    </table>
    {isAdmin ?
      <button style={{ width: 'max-content' }} type="button" className="btn btn-outline-primary" onClick={handleAdd}>
        {window.ifunds.strings.addColumn}
      </button>
      : undefined
    }
  </>;
};

export default FilterConfigurator;
